import { useRef, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { invokeAgent } from '@/api/agent';
import type { ChatMessage } from '@/types/chat';
import A2UIRenderer from '@/components/A2UIRenderer/A2UIRenderer';
import './ChatWidget.css';

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  role: 'bot',
  text: "Hi! I'm TOBi, your Vodacom virtual assistant. How can I help you today?",
};

function renderMarkdown(text: string): { __html: string } {
  return { __html: DOMPurify.sanitize(marked.parse(text, { async: false }) as string) };
}

let nextId = 0;
function newId(): string {
  nextId += 1;
  return `msg-${Date.now()}-${nextId}`;
}

export interface ChatWidgetProps {
  /** Rendered in the widget header, typically the signed-in user's name and a logout action. */
  headerExtra?: React.ReactNode;
}

export default function ChatWidget({ headerExtra }: ChatWidgetProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ block: 'end' });
    });
  };

  const addMessage = (message: ChatMessage) => {
    setMessages((prev) => [...prev, message]);
    scrollToBottom();
  };

  const sendPrompt = async (promptText: string) => {
    setSending(true);
    try {
      const result = await invokeAgent(promptText);
      if (result.response?.trim()) {
        addMessage({ id: newId(), role: 'bot', text: result.response });
      }
      if (result.ui) {
        addMessage({ id: newId(), role: 'bot', ui: result.ui });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Something went wrong';
      addMessage({ id: newId(), role: 'error', text: `Error: ${message}` });
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    addMessage({ id: newId(), role: 'user', text });
    setInput('');
    await sendPrompt(text);
  };

  const handleA2uiAction = (message: string) => {
    addMessage({ id: newId(), role: 'user', text: message });
    void sendPrompt(message);
  };

  return (
    <div className="chat">
      <div className="chat-header">
        <span>TOBi — Vodacom Assistant</span>
        {headerExtra}
      </div>
      <div className="messages">
        {messages.map((message) => {
          if (message.ui) {
            return (
              <div key={message.id} className="msg msg-ui">
                <A2UIRenderer ui={message.ui} onAction={handleA2uiAction} />
              </div>
            );
          }
          if (message.role === 'user') {
            return (
              <div key={message.id} className="msg user">
                {message.text}
              </div>
            );
          }
          return (
            <div
              key={message.id}
              className={`msg ${message.role}`}
              dangerouslySetInnerHTML={renderMarkdown(message.text ?? '')}
            />
          );
        })}
        {sending && <div className="typing">Thinking...</div>}
        <div ref={messagesEndRef} />
      </div>
      <div className="input-area">
        <input
          type="text"
          value={input}
          placeholder="Type your message..."
          autoComplete="off"
          disabled={sending}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !sending) void handleSend();
          }}
        />
        <button type="button" disabled={sending || !input.trim()} onClick={() => void handleSend()}>
          Send
        </button>
      </div>
    </div>
  );
}
